import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Music, Play, Type } from "lucide-react";
import AudioClipLibrary from "@/components/audio/AudioClipLibrary";
import VoiceSelect from "@/components/pages/call-flows/components/VoiceSelect";
import { CallFlowPrompt } from "@/model/interface/CallFlow";
import { AudioClip } from "@/model/interface/FileResponse";
import { useGetAudioClipsQuery } from "@/store/rest_api";

interface AudioPromptPickerProps {
  label: string;
  placeholder: string;
  value?: CallFlowPrompt;
  voice?: string;
  onChange: (prompt: CallFlowPrompt) => void;
  onVoiceChange?: (voice: string, language: string) => void;
}

export default function AudioPromptPicker({
  label,
  placeholder,
  value,
  voice,
  onChange,
  onVoiceChange,
}: AudioPromptPickerProps) {
  const { data: clips } = useGetAudioClipsQuery();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const mode = value?.mode ?? "SAY";
  const selectedClip = clips?.find((clip) => clip.id === value?.audioClipId);

  const handleSelectClip = (clip: AudioClip) => {
    onChange({ mode: "PLAY", audioClipId: clip.id, audioClipName: clip.name });
    setIsLibraryOpen(false);
  };

  const handleTextChange = (text: string) => {
    onChange({ mode: "SAY", text });
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === "SAY" ? "default" : "outline"}
          size="sm"
          onClick={() => onChange({ mode: "SAY", text: value?.text ?? "" })}
        >
          <Type className="mr-2 h-4 w-4" />
          Text to speech
        </Button>
        <Button
          type="button"
          variant={mode === "PLAY" ? "default" : "outline"}
          size="sm"
          onClick={() => setIsLibraryOpen(true)}
        >
          <Music className="mr-2 h-4 w-4" />
          Audio clip
        </Button>
      </div>

      {mode === "SAY" && (
        <div className="space-y-2">
          <Textarea
            placeholder={placeholder}
            value={value?.text ?? ""}
            onChange={(changeEvent) => handleTextChange(changeEvent.target.value)}
            rows={3}
          />
          {onVoiceChange && <VoiceSelect value={voice} previewText={value?.text} onChange={onVoiceChange} />}
        </div>
      )}

      {mode === "PLAY" && (
        <div className="space-y-2 rounded-md border p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Play className="h-4 w-4 text-muted-foreground" />
              <span>{selectedClip?.name ?? value?.audioClipName ?? "Selected clip"}</span>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setIsLibraryOpen(true)}>
              Change clip
            </Button>
          </div>
          {selectedClip && <audio controls src={selectedClip.url} className="w-full" />}
        </div>
      )}

      <Dialog open={isLibraryOpen} onOpenChange={setIsLibraryOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Choose an audio clip</DialogTitle>
            <DialogDescription>
              Pick a clip from your library, record one with your microphone, or upload a file.
            </DialogDescription>
          </DialogHeader>
          <AudioClipLibrary onSelect={handleSelectClip} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
